import React, { useContext, useState } from "react";
import { Bar, Line } from "react-chartjs-2";

import { ThemeContext, themes } from "../../contexts/ThemeContext";
import { bucketLabel, formatMoney, formatNumber } from "./analyticsModel";

// Validated categorical slots (dataviz reference palette, checked against the
// CMS surfaces #262a3f dark / #ffffff light). Series colour follows the series,
// never its rank. The previous period is a comparison, so it is neutral grey.
const PALETTE = {
  dark: { series1: "#3987e5", series2: "#d95926", compare: "#8a92a8", grid: "rgba(255,255,255,0.07)", ink: "#9aa3b8", surface: "#262a3f" },
  light: { series1: "#2a78d6", series2: "#eb6834", compare: "#94a0b4", grid: "rgba(15,23,42,0.07)", ink: "#5b6b82", surface: "#ffffff" },
};

export const useChartPalette = () => {
  const { theme } = useContext(ThemeContext);
  return theme === themes.light ? PALETTE.light : PALETTE.dark;
};

const baseOptions = (palette, { money, lang, legend }) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 250 },
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: legend
      ? {
          display: true,
          position: "bottom",
          align: "start",
          labels: { color: palette.ink, boxWidth: 14, boxHeight: 2, usePointStyle: false, padding: 14 },
        }
      : { display: false },
    tooltip: {
      padding: 10,
      cornerRadius: 8,
      callbacks: {
        label: (context) =>
          `${context.dataset.label}: ${money ? formatMoney(context.parsed.y, lang) : formatNumber(context.parsed.y, lang)}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: palette.ink, maxTicksLimit: 8, maxRotation: 0, autoSkipPadding: 12 },
    },
    y: {
      beginAtZero: true,
      grid: { color: palette.grid, drawBorder: false },
      ticks: {
        color: palette.ink,
        maxTicksLimit: 5,
        precision: money ? undefined : 0,
        callback: (value) => (money ? formatMoney(value, lang, { compact: true }) : formatNumber(value, lang)),
      },
    },
  },
});

/** Wraps a chart with an accessible data-table toggle. */
export const ChartFrame = ({ children, table, tt, height = 260, label }) => {
  const [showTable, setShowTable] = useState(false);
  return (
    <figure className="an-chart">
      <div className="an-chart__canvas" style={{ height }} role="img" aria-label={label}>
        {children}
      </div>
      {table ? (
        <figcaption className="an-chart__caption">
          <button type="button" className="pc-link" onClick={() => setShowTable((value) => !value)} aria-expanded={showTable}>
            {showTable ? tt("hideTable") : tt("showTable")}
          </button>
          {showTable ? <div className="an-chart__table">{table}</div> : null}
        </figcaption>
      ) : null}
    </figure>
  );
};

const PointTable = ({ rows, columns }) => (
  <table className="an-table is-dense">
    <thead>
      <tr>
        {columns.map((column) => (
          <th key={column.key} scope="col" className={column.end ? "is-end" : undefined}>
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, index) => (
        <tr key={index}>
          {columns.map((column) => (
            <td key={column.key} className={column.end ? "is-end" : undefined}>
              {column.render(row)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * Current period (solid) against the equal previous period (dashed grey),
 * index-aligned. One measure, one axis.
 */
export const TrendChart = ({ points, field, granularity, label, money = true, lang, tt, height, showPrevious = true }) => {
  const palette = useChartPalette();
  const dense = points.length > 45;
  const datasets = [
    {
      label: `${label} · ${tt("currentPeriod")}`,
      data: points.map((point) => point[field]),
      borderColor: palette.series1,
      backgroundColor: palette.series1,
      borderWidth: 2,
      pointRadius: dense ? 0 : 3,
      pointHoverRadius: 5,
      pointBorderColor: palette.surface,
      pointBorderWidth: 2,
      cubicInterpolationMode: "monotone",
      spanGaps: true,
    },
  ];
  if (showPrevious) {
    datasets.push({
      label: `${label} · ${tt("previousPeriod")}`,
      data: points.map((point) => point.previous?.[field] ?? null),
      borderColor: palette.compare,
      backgroundColor: palette.compare,
      borderWidth: 2,
      borderDash: [5, 4],
      pointRadius: 0,
      pointHoverRadius: 4,
      cubicInterpolationMode: "monotone",
      spanGaps: true,
    });
  }

  const format = (value) => (money ? formatMoney(value, lang) : formatNumber(value, lang));
  return (
    <ChartFrame
      tt={tt}
      height={height}
      label={label}
      table={
        <PointTable
          rows={points}
          columns={[
            { key: "bucket", label: tt("col_date"), render: (row) => bucketLabel(row.bucket, granularity, lang) },
            { key: "current", label: tt("currentPeriod"), end: true, render: (row) => format(row[field]) },
            ...(showPrevious
              ? [{ key: "previous", label: tt("previousPeriod"), end: true, render: (row) => format(row.previous?.[field]) }]
              : []),
          ]}
        />
      }
    >
      <Line
        data={{ labels: points.map((point) => bucketLabel(point.bucket, granularity, lang)), datasets }}
        options={baseOptions(palette, { money, lang, legend: showPrevious })}
      />
    </ChartFrame>
  );
};

/** Single-series bar chart (weekday, hour). */
export const SimpleBarChart = ({ labels, values, label, money = true, lang, tt, height = 220, tone = "series1" }) => {
  const palette = useChartPalette();
  const format = (value) => (money ? formatMoney(value, lang) : formatNumber(value, lang));
  return (
    <ChartFrame
      tt={tt}
      height={height}
      label={label}
      table={
        <PointTable
          rows={labels.map((name, index) => ({ name, value: values[index] }))}
          columns={[
            { key: "name", label: label, render: (row) => row.name },
            { key: "value", label: tt("revenue"), end: true, render: (row) => format(row.value) },
          ]}
        />
      }
    >
      <Bar
        data={{
          labels,
          datasets: [
            {
              label,
              data: values,
              backgroundColor: palette[tone],
              borderRadius: { topLeft: 4, topRight: 4 },
              borderSkipped: "bottom",
              maxBarThickness: 28,
              categoryPercentage: 0.8,
              barPercentage: 0.9,
            },
          ],
        }}
        options={{ ...baseOptions(palette, { money, lang, legend: false }), interaction: { intersect: false, mode: "index" } }}
      />
    </ChartFrame>
  );
};
